/**
 * QuickReplySelector Component
 *
 * Modal for selecting and applying quick reply templates with variable substitution.
 * Allows browsing templates by category and applying them to messages.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-481 - Quick Reply Templates (6 SP)
 * Location: apps/web/src/components/messages/QuickReplySelector.jsx
 */

import React, { useState, useEffect } from 'react';
import apiRequest from '../../services/api';

/**
 * QuickReplySelector Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onSelectTemplate - Callback when template is selected (content) => void
 * @param {Object} props.variables - Variables for substitution (customer_name, guide_name, etc.)
 */
const QuickReplySelector = ({
  isOpen,
  onClose,
  onSelectTemplate,
  variables = {},
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest('/api/v1/messages/templates', { method: 'GET' });

      if (response.success) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error('[QuickReplySelector] Failed to fetch templates:', err);
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load templates');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      // Apply template with variable substitution
      const response = await apiRequest(`/api/v1/messages/templates/${template.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ variables }),
      });

      if (response.success) {
        onSelectTemplate(response.data.content);
        onClose();
      }
    } catch (err) {
      console.error('[QuickReplySelector] Failed to apply template:', err);
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      setTimeout(() => setError(null), 3000);
      } else {
        setError(err.message || 'Failed to apply template');
      setTimeout(() => setError(null), 3000);
      }
    }
  };

  // Filter templates by category
  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter((t) => t.category === selectedCategory);

  // Get unique categories
  const categories = ['all', ...new Set(templates.map((t) => t.category))];

  if (!isOpen) {return null;}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Quick Replies</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Category Tabs */}
        <div className="flex space-x-2 p-4 border-b overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && filteredTemplates.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <p className="text-sm">No templates found</p>
              <p className="text-xs text-gray-400 mt-1">Create templates to use quick replies</p>
            </div>
          )}

          {!loading && !error && filteredTemplates.length > 0 && (
            <div className="space-y-2">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={() => handleSelectTemplate(template)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <p className="text-xs text-gray-600">
            Templates support variables: <code className="bg-gray-200 px-1 py-0.5 rounded">{'{{customer_name}}'}</code>,{' '}
            <code className="bg-gray-200 px-1 py-0.5 rounded">{'{{guide_name}}'}</code>,{' '}
            <code className="bg-gray-200 px-1 py-0.5 rounded">{'{{start_date}}'}</code>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * TemplateCard Component
 */
const TemplateCard = ({ template, onSelect }) => {
  return (
    <button
      onClick={onSelect}
      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">{template.name}</h3>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {template.category}
        </span>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
        {template.content}
      </p>
      {template.usage_count > 0 && (
        <p className="text-xs text-gray-400 mt-2">Used {template.usage_count} times</p>
      )}
    </button>
  );
};

export default QuickReplySelector;
